import PlaceTypeForm             from '@/forms/services/map/PlaceTypeForm';
import { PlaceTypeTitle }        from '@/forms/services/map/PlaceTypeForm';
import PlaceTypeModel            from '@/models/services/map/PlaceTypeModel';
import currentProjectEditorStore from '@/TokamapEditor/stores/currentProjectEditorStore';
import leftSidebarStore          from '@/TokamapEditor/stores/leftSidebarStore';
import ListEmptyState            from '@/TokamapEditor/ux/ListEmptyState';
import placeTypeDeletionBlockedMessage from '@/forms/services/map/placeTypeDeletion';
import ListCollection            from '@widesk-ui/components/listCollection/ListCollection';
import { observer }              from 'mobx-react-lite';
import InputBooleanUncheckable   from '@widesk-ui/components/form/InputBooleanUncheckable';
import PlaceModel                from '@models/map/PlaceModel';
import FieldModel                from '@models/map/FieldModel';
import actionModel               from '@widesk-ui/tools/actionModel';
import { DeleteOutlined }        from '@ant-design/icons';
import { PlusOutlined }          from '@ant-design/icons';
import View                      from '@widesk-ui/components/View';
import Button                    from '@widesk-ui/components/Button';
import useDesignTokens           from '@/hooks/useDesignTokens';
import { Modal }                 from '@widesk-ui/components/modal/Modal';
import { message }               from '@widesk-ui/hooks/useMessage';
import nextFreeLabel             from '@/tools/nextFreeLabel';
import messageWithUndo           from '@/TokamapEditor/helpers/messageWithUndo';
import { pickFreeColor }         from '@/TokamapEditor/helpers/randomColor';
import React                     from 'react';
import ApiCollection             from '@widesk-core/models/ApiCollection';
import PlaceTypeFieldModel       from '@models/map/PlaceTypeFieldModel';
import currentEditorStore        from '@/TokamapEditor/stores/currentEditorStore';
import projectShapesStore        from '@/TokamapEditor/stores/projectShapesStore';
import ZoneCountButton           from '@/TokamapEditor/ux/ZoneCountButton';
import ShapeHoverRow             from '@/TokamapEditor/ux/ShapeHoverRow';
import { ShapeHoverFilter }       from '@/TokamapEditor/ux/ShapeHoverRow';
import { Shape }                 from '@tokamap/core';
import CompactControls from '@/TokamapEditor/ux/CompactControls';

/**
 * Ouvre un type et, avec lui, les champs qui composent sa fiche : `PlaceTypeForm` porte déjà la liste
 * ordonnée des champs et sait en créer un à la volée. C'est ce qui permet à la barre du bas de ne
 * plus exposer "Champs" en propre — un champ n'existe utilement qu'attaché à un type.
 */
const openPlaceType = (m: PlaceTypeModel) => leftSidebarStore.lowerOpen({
	closeButton: true,
	// Un peu plus étroit que les 400px par défaut : ce formulaire n'aligne que des lignes courtes (une
	// couleur, un champ, sa nature). Réduit d'autant le décalage imposé à la liste derrière.
	width: 380,
	parentTitle: PlaceTypeModel.localize('plural'),
	title: <PlaceTypeTitle model={m} />,
	// `inSidebar` : le formulaire peut alors prendre la main sur cet en-tête pour y afficher ses
	// sous-écrans (voir l'ajout d'un champ). Il ne le fait pas quand il est rendu en modale.
	render: () => <PlaceTypeForm inSidebar model={m} projectUrn={m.getUrn('project')} />,
});

/**
 * Crée le type tout de suite, sous un nom généré, puis ouvre sa fiche.
 *
 * Plus de formulaire de création à remplir avant d'avoir rien vu : la fiche qui s'ouvre enregistre
 * au fil de l'eau, et le nom s'y corrige d'un clic dans l'en-tête. Même geste que pour les champs et
 * pour les thèmes (`addAndEditTheme`).
 */
const createPlaceType = async (projectUrn: Urn) => {
	try {
		const listStore = leftSidebarStore.getListStore('placeTypes');
		const existing = (listStore?.data || []) as PlaceTypeModel[];

		const model = new PlaceTypeModel();
		// "Type" et non "Nouveau type" : le nom est destiné à être remplacé tout de suite, et "Nouveau"
		// resterait à tort sur celui qu'on oublie de renommer. Même parti pris que pour les champs,
		// nommés d'après leur nature.
		const label = nextFreeLabel('Nouveau type', existing.map(m => m.label).filter(Boolean));

		// La couleur est obligatoire côté API. On pioche la première libre de la palette plutôt qu'une
		// constante : deux types créés d'affilée seraient sinon indiscernables sur le plan. Les couleurs
		// prises sont celles de la page affichée — au pire une collision avec un type d'une autre page,
		// que la fiche permet de corriger d'un clic.
		const color = pickFreeColor(existing.map(m => m.color).filter(Boolean));

		await model.patch({ label, color, project: projectUrn });

		await listStore?.reload();

		openPlaceType(model);

		messageWithUndo(<span>« {label} » a été créé.</span>, async () => {
			await model.destroy();

			leftSidebarStore.pop();
			await listStore?.reload();
		});
	} catch (err) {
		message.exception(err);
	}
};

const PlaceTypeListSidebar = observer(() => {
	const projectEditor = currentProjectEditorStore.get;
	const project = projectEditor.project;
	const tokens = useDesignTokens();

	/**
	 * Nombre de champs par type, indexé par URN de type.
	 *
	 * UNE requête pour toute la page, pas une par ligne : `PlaceTypeFieldModel` se filtre par
	 * `placeType`, on lui passe donc les ids affichés d'un coup et on regroupe ici. Le volume est borné
	 * par types × champs, donc négligeable.
	 *
	 * Pas de compteur de points d'intérêts en revanche : il demanderait une requête par type pour lire
	 * son `totalItems`, ou le rapatriement de tous les points d'intérêts du projet. À faire côté API,
	 * en sérialisant le compte sur le type, pas ici.
	 */
	const [fieldCounts, setFieldCounts] = React.useState<Record<string, number>>({});

	/**
	 * Types de la page qui portent au moins un point d'intérêt.
	 *
	 * Un booléen et pas un compte : l'API sait répondre « lesquels sont liés » en UNE requête
	 * (`exists[places]` croisé avec les ids affichés), là où le nombre par type en demanderait une par
	 * ligne. C'est tout ce qu'il faut pour barrer la suppression — la fiche du type, elle, connaît le
	 * compte exact et le dit.
	 */
	const [typeUrnsWithPlaces, setTypeUrnsWithPlaces] = React.useState<Set<string>>(new Set());

	/**
	 * Type de chaque point d'intérêt POSÉ sur la carte ouverte, indexé par id de point d'intérêt.
	 *
	 * Rien ne relie une forme à un type : une forme pointe un point d'intérêt (`entity.entity.id`), et
	 * c'est lui qui porte le type. On demande donc les types des seuls points d'intérêts présents sur
	 * la carte — leur nombre est borné par les formes dessinées, pas par la taille du projet, là où
	 * partir des types aurait ramené tous les points d'intérêts de chacun.
	 */
	const [typeUrnByPlaceId, setTypeUrnByPlaceId] = React.useState<Record<string, string>>({});

	const placeIdsOnMap = [...new Set(
		[
			...currentEditorStore.allShapes,
			...projectEditor.mapCollection.models
				.filter(map => String(map.id) !== String(projectEditor.currentMap.id))
				.flatMap(map => projectShapesStore.shapesOfMap(map.id)),
		]
			.filter(shape => shape.action === 'open_place')
			.map(shape => String((shape.entity as any)?.entity?.id || ''))
			.filter(Boolean),
	)];

	React.useEffect(() => {
		if (!placeIdsOnMap.length) return setTypeUrnByPlaceId({});

		const places = new ApiCollection(PlaceModel);

		places
			.listBy(placeIdsOnMap, 'id')
			.then(() => setTypeUrnByPlaceId(Object.fromEntries(
				places.models.map(place => [String(place.id), place.getUrn('placeType')]),
			)))
			.catch(err => console.error('Types des points d\'intérêts de la carte', err));
	}, [placeIdsOnMap.join(',')]);

	const isShapeOfType = (shape: Shape, placeTypeUrn: string) => (
		shape.action === 'open_place'
		&& typeUrnByPlaceId[String((shape.entity as any)?.entity?.id || '')] === placeTypeUrn
	);

	// `minHeight: 100%` et non `height` : le conteneur remplit le tiroir quand la liste est courte —
	// ce qui pousse le bouton en bas — mais s'allonge librement quand elle déborde. Le corps du
	// tiroir reste le conteneur de défilement, `List` s'appuyant dessus.
	return (
		<CompactControls>
			<View style={{ minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
				<View flex>
					<ShapeHoverFilter.Provider value={(m: PlaceTypeModel) => shape => isShapeOfType(shape, m.urn)}>
						<ListCollection
					RowComponent={ShapeHoverRow}
					emptyText={(
						<ListEmptyState
							title={`Aucun ${PlaceTypeModel.localize('default', 'l')}`}
							hint={`Un type regroupe les ${PlaceModel.localize('plural', 'l')} qui se ressemblent et décide des champs à renseigner.`}
						/>
					)}
					// La ligne entière ouvre le type — les champs de sa fiche étaient jusqu'ici à deux clics,
					// derrière le menu `…`, alors qu'ils sont la raison principale d'ouvrir un type. Les cases
					// à cocher restent cliquables pour la sélection multiple.
					onClickItem={m => openPlaceType(m as PlaceTypeModel)}
					clickable
					manualActiveIndex
					onDidMount={listStore => {
						if (!leftSidebarStore.getListStore('placeTypes')) {
							leftSidebarStore.setListStore('placeTypes', listStore);
						}
					}}
					onFetchCollectionStart={async coll => {
						coll.setRequiredFilter('project', project.id);
					}}
					onFetchCollectionEnd={async coll => {
						const ids = coll.models.map(m => m.id).filter(Boolean);

						if (!ids.length) {
							setTypeUrnsWithPlaces(new Set());

							return setFieldCounts({});
						}

						const links = new ApiCollection(PlaceTypeFieldModel);
						await links.list({ params: { 'placeType': ids, 'itemsPerPage': 1000 } });

						const counts: Record<string, number> = {};
						links.forEach(link => {
							const urn = link.getUrn('placeType');
							if (urn) counts[urn] = (counts[urn] || 0) + 1;
						});

						setFieldCounts(counts);

						// Les types de la page qui portent des points d'intérêts, en UNE requête : on redemande les
						// mêmes ids en y ajoutant `exists[places]` — un filtre que l'API expose (voir
						// `PlaceTypeModel._tsFilters`) — et ceux qui reviennent sont les liés.
						const linked = new ApiCollection(PlaceTypeModel);
						await linked.setFilter('exists[places]', true).listBy(ids, 'id');

						setTypeUrnsWithPlaces(new Set(linked.models.map(m => m.urn)));
					}}
					modelClass={PlaceTypeModel}
					itemProps={(m, listStore) => ({
						actions: [
							actionModel.delete(m, {
								// Grisée plutôt que retirée quand le type est encore lié : l'API refuserait, et
								// l'action absente aurait laissé croire qu'un type ne se supprime jamais.
								actionProps: {
									label: 'Supprimer',
									disabled: typeUrnsWithPlaces.has(m.urn) || !!fieldCounts[m.urn],
									disabledMessage: placeTypeDeletionBlockedMessage(
										typeUrnsWithPlaces.has(m.urn),
										fieldCounts[m.urn],
									),
								},
								callback: async () => listStore.reload(),
							}),
						],
						title: m.renderName(),
						subTitle: (
							<View size="small" color={tokens.text.muted}>
								{fieldCounts[m.urn] || 0} {FieldModel.localize((fieldCounts[m.urn] || 0) > 1 ? 'plural' : 'default', 'l')}
							</View>
						),
						// Même affordance que dans la liste des points d'intérêts : un bouton qui annonce le compte
						// et met en évidence au clic, désactivé quand il n'y a rien à montrer.
						renderRight: () => (
							<ZoneCountButton
								filter={shape => isShapeOfType(shape, m.urn)}
								suffix={count => `associée${count > 1 ? 's' : ''}`}
							/>
						),
						extraProps: { paddingH: 20, paddingV: 12 },
					})}
					massActions={(selected, listStore) => {
						// Grisée dès qu'un seul type de la sélection est encore lié : la suppression part en
						// `Promise.all`, un refus de l'API sur l'un d'eux laisserait les autres supprimés et le
						// message d'erreur ne dirait pas lesquels.
						const blocked = selected.filter(m => typeUrnsWithPlaces.has(m.urn) || !!fieldCounts[m.urn]);

						return [
							{
								danger: true,
								icon: <DeleteOutlined />,
								label: `Supprimer la sélection (${selected.length})`,
								disabled: !!blocked.length,
								disabledMessage: blocked.length === 1
									? `« ${blocked[0]!.label} » est encore lié. Détachez-le ou retirez-le de la sélection.`
									: blocked.length
										? `${blocked.length} types de la sélection sont encore liés. Détachez-les ou retirez-les de la sélection.`
										: undefined,
								onClick: async () => {
									const modelsLabel = PlaceTypeModel.localize(selected.length > 1 ? 'plural' : 'default', 'l');
									const numberModelsLabel = `${selected.length} ${modelsLabel}`;
									return Modal.confirm({
										okText: 'Confirmer la suppression',
										okType: 'danger',
										title: `Supprimer ${numberModelsLabel} ?`,
										onOk: async () => {
											try {
												await Promise.all(selected.map(placeType => placeType.destroy()));
												message.success(`La sélection de ${numberModelsLabel} a bien été supprimée.`);
												return listStore.reload();
											} catch (err) {
												message.exception(err);
											}
										},
									});
								},
							},
						];
					}}
					collectionSorts={{
						label: {
							labels: ['De A à Z', 'De Z à A'],
							value: 'label',
						},
						createdAt: {
							labels: ['Du plus récent au plus ancien', 'Du plus ancien au plus récent'],
							value: 'createdAt',
						},
					}}
					collectionFilters={{
						hasPlaces: {
							label: `Lié à des ${PlaceModel.localize('plural', 'l')}`,
							element: <InputBooleanUncheckable />,
							filter: 'exists[places]',
							span: 12,
						},
						hasPlaceTypeFields: {
							label: `Lié à des ${FieldModel.localize('plural', 'l')}`,
							element: <InputBooleanUncheckable />,
							filter: 'exists[placeTypeFields]',
							span: 12,
						},
					}}
						defaultSortName="label"
						/>
					</ShapeHoverFilter.Provider>
				</View>

				{/* Collé en bas de la zone de défilement plutôt qu'en pied de tiroir : `List` y place déjà sa
				    pagination, et le premier arrivé occupe la place. Il reste ainsi visible quelle que soit
				    la longueur de la liste, sans la lui prendre. */}
				<View
					p="sm"
					bg={tokens.surface.card}
					style={{ position: 'sticky', bottom: 0, borderTop: `1px solid ${tokens.border.subtle}` }}
				>
					{/* Marges resserrées et bouton agrandi : l'ensemble occupe au total moins de hauteur
					    qu'avant, tout en donnant plus de surface cliquable à l'action principale. */}
					<Button
						block
						size="large"
						type="primary"
						icon={<PlusOutlined />}
						onClick={() => createPlaceType(project.urn)}
					>
						{`Ajouter ${PlaceTypeModel.localize('a', 'l')}`}
					</Button>
				</View>
			</View>
		</CompactControls>
	);
});

export default PlaceTypeListSidebar;